import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';

const getId = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value._id !== undefined) return String(value._id);
  return String(value);
};

const normalizeProviders = (values) =>
  [...new Set((values || []).map((value) => String(value || '').trim()).filter(Boolean))];

const EnvironmentImportTargets = ({
  snapshot,
  providerOptions,
  providersLoading,
  targetsByCompetition,
  onTargetsChange,
  onRunCheck,
  checkLoading,
  gameFactStrategy,
  onGameFactStrategyChange,
}) => {
  const [applyAllProviders, setApplyAllProviders] = useState([]);
  const competitions = Array.isArray(snapshot?.data?.competitions) ? snapshot.data.competitions : [];

  const incompleteCompetitionIds = useMemo(
    () =>
      competitions
        .map((competition) => getId(competition?._id))
        .filter((competitionId) => !(targetsByCompetition?.[competitionId] || []).length),
    [competitions, targetsByCompetition]
  );

  const canCheck = competitions.length > 0 && incompleteCompetitionIds.length === 0 && Boolean(gameFactStrategy) && !checkLoading;

  const handleCompetitionChange = (competitionId, providers) => {
    onTargetsChange({
      ...targetsByCompetition,
      [competitionId]: normalizeProviders(providers),
    });
  };

  const applyToAll = () => {
    const normalized = normalizeProviders(applyAllProviders);
    if (!normalized.length) return;

    const next = { ...targetsByCompetition };
    competitions.forEach((competition) => {
      const competitionId = getId(competition?._id);
      if (competitionId) next[competitionId] = normalized;
    });
    onTargetsChange(next);
  };

  return (
    <Card>
      <CardContent>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h6">4. Providers cibles</Typography>
            <Typography variant="body2" color="text.secondary">
              Choisis 1 à N providers cibles pour chaque compétition. Le même provider peut être appliqué à plusieurs compétitions : les équipes et joueurs partagés seront alors mutualisés dans le dry-run.
            </Typography>
          </Box>

          <Alert severity="info">
            Aucune écriture n'est réalisée à cette étape. Le provider du snapshot reste une information source ; seules les sélections ci-dessous définissent la destination.
          </Alert>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
              gap: 1.5,
              alignItems: 'start',
            }}
          >
            <Autocomplete
              multiple
              freeSolo
              disableCloseOnSelect
              options={providerOptions}
              value={applyAllProviders}
              loading={providersLoading}
              onChange={(_, values) => setApplyAllProviders(normalizeProviders(values))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Providers cibles à appliquer à toutes les compétitions"
                  placeholder="Ex. est-eclair, union"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {providersLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="outlined"
              startIcon={<SyncAltOutlinedIcon />}
              onClick={applyToAll}
              disabled={!applyAllProviders.length}
              sx={{ minHeight: 56 }}
            >
              Appliquer à toutes les compétitions
            </Button>
          </Box>

          <Divider />

          <Stack spacing={1.5}>
            {competitions.map((competition) => {
              const competitionId = getId(competition?._id);
              const values = targetsByCompetition?.[competitionId] || [];

              return (
                <Box
                  key={competitionId || competition?.name}
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: values.length ? 'divider' : 'warning.light',
                    borderRadius: 1,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 0.8fr) minmax(320px, 1.2fr)' },
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography fontWeight={600}>{competition?.name || 'Compétition sans nom'}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {competition?.season || 'Saison non renseignée'}
                      {competition?.thirdPartyEntityId ? ` · ${competition.thirdPartyEntityId}` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Source : {snapshot?.manifest?.sourceProvider || '—'}
                    </Typography>
                  </Box>

                  <Autocomplete
                    multiple
                    freeSolo
                    disableCloseOnSelect
                    options={providerOptions}
                    value={values}
                    loading={providersLoading}
                    onChange={(_, providers) => handleCompetitionChange(competitionId, providers)}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => (
                        <Chip label={option} size="small" {...getTagProps({ index })} key={`${competitionId}-${option}`} />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Provider(s) cible(s)"
                        required
                        error={!values.length}
                        helperText={!values.length ? 'Choisis au moins un provider cible.' : `${values.length} destination(s)`}
                      />
                    )}
                  />
                </Box>
              );
            })}
          </Stack>

          <Divider />

          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Stratégie des faits de jeu
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Ce choix concerne uniquement les Matchs qui existent déjà dans l'environnement cible. Pour un Match créé par l'import, les faits de jeu du snapshot seront toujours créés.
            </Typography>

            <FormControl required error={!gameFactStrategy}>
              <FormLabel>Faits de jeu des Matchs déjà existants</FormLabel>
              <RadioGroup
                value={gameFactStrategy || ''}
                onChange={(event) => onGameFactStrategyChange(event.target.value)}
              >
                <FormControlLabel
                  value="replace"
                  control={<Radio />}
                  label="Remplacer les faits de jeu des Matchs existants (recommandé)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5, mb: 1 }}>
                  Les faits de jeu actuellement rattachés au Match cible seront remplacés par ceux du snapshot. Techniquement, les faits existants sont supprimés puis les faits du snapshot sont recréés. En cas d’échec de création, le backend tente de restaurer les anciens faits.
                </Typography>

                <FormControlLabel
                  value="skip"
                  control={<Radio />}
                  label="Ne pas importer les faits de jeu sur les Matchs existants"
                />
                <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                  Les faits de jeu des Matchs déjà présents en cible restent strictement inchangés.
                </Typography>
              </RadioGroup>
            </FormControl>

            {!gameFactStrategy && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Choisis une stratégie pour les faits de jeu avant de lancer le pré-check.
              </Alert>
            )}

            {gameFactStrategy === 'replace' && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Le rapport de pré-check indiquera précisément combien de faits de jeu existants seront remplacés. Les faits locaux absents du snapshot seront donc supprimés pour les Matchs concernés.
              </Alert>
            )}

            {gameFactStrategy === 'skip' && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Les Matchs existants pourront être créés ou mis à jour normalement, mais leurs faits de jeu ne seront pas modifiés.
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={incompleteCompetitionIds.length ? 'warning.main' : 'success.main'}>
              {incompleteCompetitionIds.length
                ? `${incompleteCompetitionIds.length} compétition(s) sans destination.`
                : !gameFactStrategy
                ? 'Choisis aussi une stratégie pour les faits de jeu.'
                : 'Toutes les compétitions ont une destination et la stratégie GameFact est configurée.'}
            </Typography>

            <Button
              variant="contained"
              startIcon={checkLoading ? <CircularProgress size={18} color="inherit" /> : <FactCheckOutlinedIcon />}
              disabled={!canCheck}
              onClick={onRunCheck}
            >
              {checkLoading ? 'Analyse de la destination...' : 'Pré-checker l’import'}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EnvironmentImportTargets;
